import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useModules } from "../context/ModulesContext";
import { recordPortalModuleConfirmation } from "../helpers/apiHelper";
import {
  MODULE_TYPES,
  findActiveMarianaAnnouncementModule,
  getModuleContext,
} from "../modules/moduleUtils";
import "../styles/MarianaAnnouncementGate.css";

// The notice is shown on every login, so dismissal is scoped to the browser
// session: navigating or reloading will not bring it back, signing in again will.
const DISMISSED_STORAGE_KEY = "bhp:mariana_announcement_dismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeDismissed = (value) => {
  try {
    if (value) {
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, "true");
    } else {
      sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable - fall back to in-memory state only.
  }
};

const getAuthFlow = () => {
  return (
    localStorage.getItem("authFlow") || sessionStorage.getItem("authFlow") || ""
  );
};

const getAccessToken = (tokens) => {
  return (
    tokens?.accessToken ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    ""
  );
};

const ANNOUNCEMENT_TITLE = "Atenção clientes e advogados colaboradores:";

const ANNOUNCEMENT_PARAGRAPHS = [
  "Sabemos que as notícias recentes sobre o Comitê de Clientes podem ter gerado dúvidas ou preocupações.",
  "Por isso, queremos deixar uma mensagem clara:",
];

const ANNOUNCEMENT_HEADLINE = "O caso inglês Mariana continua.";

const ANNOUNCEMENT_BODY = [
  "A decisão do Comitê de Clientes não encerra os processos de nossos clientes. O PGMBM continua representando centenas de milhares de autores, e o processo segue avançando para a Fase 2, etapa em que os diferentes tipos de danos e os pedidos de indenização serão avaliados.",
  "O julgamento da Fase 2 está atualmente previsto para começar em abril de 2027.",
  "Enquanto isso, nossas equipes continuam trabalhando na preparação necessária para essa próxima etapa, incluindo documentos e provas, casos modelos, provas periciais, questões jurídicas e a avaliação dos diferentes tipos de danos.",
  "Também foi recentemente garantido um financiamento adicional de US$ 150 milhões para apoiar o trabalho necessário para a próxima fase do processo.",
  "Se você é cliente ou advogado colaborar do Pogust Goodhead (“PGMBM”), não precisa tomar nenhuma medida em razão da decisão do Comitê de Clientes.",
  "Continue acompanhando nossas comunicações oficiais e mantenha seus dados de contato atualizados.",
  "Seguimos focados em proteger os interesses de nossos clientes e avançar com o processo.",
];

export default function MarianaAnnouncementGate({ children }) {
  const { isLoggedIn, tokens } = useAuth();
  const { modules, modulesInitialized, refreshModules } = useModules();
  const [confirming, setConfirming] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);

  const isPortalUserFlow = getAuthFlow() === "portal_user";
  const accessToken = getAccessToken(tokens);

  const activeModule = useMemo(() => {
    return findActiveMarianaAnnouncementModule(modules);
  }, [modules]);

  const open = Boolean(
    isLoggedIn &&
      isPortalUserFlow &&
      modulesInitialized &&
      activeModule &&
      !dismissed,
  );

  useEffect(() => {
    if (!isLoggedIn) {
      writeDismissed(false);
      setDismissed(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handleConfirm = async () => {
    if (confirming) return;

    const context = getModuleContext(activeModule);
    const userId = context?.userId || activeModule?.instanceKey || "";
    const instanceKey = activeModule?.instanceKey || userId;
    const alreadyConfirmed = Boolean(context?.confirmedAt);

    setConfirming(true);
    try {
      // Only the first confirmation is recorded - never overwrite the original.
      if (!alreadyConfirmed && accessToken && userId && instanceKey) {
        await recordPortalModuleConfirmation(accessToken, {
          module: MODULE_TYPES.MARIANA_ANNOUNCEMENT,
          instanceKey,
          userId,
          contextJson: {
            announcementKey: context?.announcementKey || "",
            userId,
            confirmedAt: new Date().toISOString(),
          },
        });
        refreshModules?.();
      }
    } catch (error) {
      // Never trap the client behind a failed write - the notice is shown again
      // next time they sign in, and the confirmation is recorded then.
      console.error("Failed to record Mariana announcement confirmation:", error);
    } finally {
      writeDismissed(true);
      setDismissed(true);
      setConfirming(false);
    }
  };

  return (
    <>
      {children}
      {open ? (
        <div className="marianaAnnouncementOverlay">
          <div
            className="marianaAnnouncementPanel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="marianaAnnouncementTitle"
          >
            <div className="marianaAnnouncementScroll">
              <h1
                className="marianaAnnouncementTitle"
                id="marianaAnnouncementTitle"
              >
                {ANNOUNCEMENT_TITLE}
              </h1>

              {ANNOUNCEMENT_PARAGRAPHS.map((paragraph) => (
                <p className="marianaAnnouncementText" key={paragraph}>
                  {paragraph}
                </p>
              ))}

              <p className="marianaAnnouncementHeadline">
                {ANNOUNCEMENT_HEADLINE}
              </p>

              {ANNOUNCEMENT_BODY.map((paragraph) => (
                <p className="marianaAnnouncementText" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="marianaAnnouncementActions">
              <button
                type="button"
                className="marianaAnnouncementButton"
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? "Confirmando..." : "Entendi"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
